import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';
import { useCurrency } from '../../../../hooks/useCurrency';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateCurrentUser($number_of_clients: Int, $assets_under_management: Float, $years_practicing: Int, $avg_client_age: Int) {
    updateCurrentUser(number_of_clients: $number_of_clients, assets_under_management: $assets_under_management, years_practicing: $years_practicing, avg_client_age: $avg_client_age)
  }
`;

const PracticeInformationEditModal = ({ user, onClose, onSave, onError, isReadOnly = false, companyName = 'your firm' }) => {
  const intl = useIntl();
  const { symbol } = useCurrency();
  const [numberOfClients, setNumberOfClients] = useState(user.number_of_clients || 0);
  const [aum, setAum] = useState(user.assets_under_management || 0);
  const [yearsPracticing, setYearsPracticing] = useState(user.years_practicing || 0);
  const [avgClientAge, setAvgClientAge] = useState(user.avg_client_age || 0);

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: (data) => {
      console.log('=== Mutation onCompleted ===', data);
      onSave('Practice information updated successfully');
    },
    onError: (error) => {
      console.error('=== Mutation onError ===', error);
      console.error('GraphQL errors:', error.graphQLErrors);
      console.error('Network error:', error.networkError);
      onError(error);
    },
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log('=== PracticeInformationEditModal handleSubmit called ===');
    console.log('Raw values:', { numberOfClients, aum, yearsPracticing, avgClientAge });

    // Convert to numbers, handle empty values properly
    const parseIntOrNull = (value) => {
      const parsed = parseInt(value);
      return isNaN(parsed) ? null : parsed;
    };

    const parseFloatOrNull = (value) => {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };

    const variables = {
      number_of_clients: parseIntOrNull(numberOfClients),
      assets_under_management: parseFloatOrNull(aum),
      years_practicing: parseIntOrNull(yearsPracticing),
      avg_client_age: parseIntOrNull(avgClientAge),
    };

    console.log('Mutation variables:', variables);

    updateUser({ variables });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-succession-bg-secondary rounded-lg p-6 max-w-2xl w-full mx-4 border border-succession-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.modals.practice_information_edit.title' })}</h2>
          <button onClick={onClose} className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors">
            <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isReadOnly && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-succession-warning/10 border border-succession-warning/30">
              <svg className="w-4 h-4 text-succession-warning shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-sm text-succession-text-primary">
                Your practice information is managed by <strong>{companyName}</strong> and updated automatically.
                {' '}You can view your data here for reference, but changes cannot be saved.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-succession-text-primary mb-2">Number of Clients</label>
            <input type="number" value={numberOfClients} onChange={(e) => setNumberOfClients(e.target.value)} disabled={isReadOnly} className={`w-full px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary disabled:cursor-not-allowed ${isReadOnly ? 'bg-succession-bg-secondary opacity-60' : 'bg-succession-bg-primary'}`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.profile_edit.modals.practice_information_edit.assets_under_management' })}</label>
            <div className={`flex items-center border border-succession-border rounded-lg ${isReadOnly ? 'bg-succession-bg-secondary opacity-60' : 'bg-succession-bg-primary'}`}>
              <span className="px-4 text-succession-text-primary select-none">{symbol}</span>
              <input type="number" value={aum} onChange={(e) => setAum(e.target.value)} disabled={isReadOnly} className="flex-1 pr-4 py-2 bg-transparent text-succession-text-primary border-0 outline-none disabled:cursor-not-allowed" style={{ border: 'none', outline: 'none' }} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.profile_edit.modals.practice_information_edit.years_practicing' })}</label>
            <input type="number" value={yearsPracticing} onChange={(e) => setYearsPracticing(e.target.value)} disabled={isReadOnly} className={`w-full px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary disabled:cursor-not-allowed ${isReadOnly ? 'bg-succession-bg-secondary opacity-60' : 'bg-succession-bg-primary'}`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.profile_edit.modals.practice_information_edit.average_client_age' })}</label>
            <input type="number" value={avgClientAge} onChange={(e) => setAvgClientAge(e.target.value)} disabled={isReadOnly} className={`w-full px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary disabled:cursor-not-allowed ${isReadOnly ? 'bg-succession-bg-secondary opacity-60' : 'bg-succession-bg-primary'}`} />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-succession-border">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors">{intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}</button>
            <div className="relative group">
              <button type="submit" disabled={loading || isReadOnly} className={`px-4 py-2 rounded-lg text-white transition-colors ${(loading || isReadOnly) ? 'bg-succession-primary opacity-50 cursor-not-allowed' : 'bg-succession-primary hover:opacity-80'}`}>
                {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.common.save_changes' })}
              </button>
              {isReadOnly && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-succession-text-primary rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  Managed by {companyName}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

PracticeInformationEditModal.propTypes = {
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default PracticeInformationEditModal;
